import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import {
  C,
  RedesignFonts,
  TITLES,
  TitleArt,
  Buy,
  ContractAddress,
  Socials,
  NAV,
  UTILITIES,
  Video,
  VIDEOS,
  SupplyDonut,
  ROADMAP,
  NDHAUS_PHOTO,
  ORIGINAL_ART,
  MEMES,
  COMIC_BOOK,
  Lettering,
} from '../kit';
import { CONTENT } from '../content';
import { ComicReader } from '../comic/ComicReader';
import { FACTS } from '../d/Tokenomics';
import { pickArt } from './art';
import { shareMeme } from './share';
import { Fireflies } from '../d/Fireflies';
import { MILESTONE_ART, yearOf } from '../roadmapArt';

const NUM_CHAPTERS = 11;

/** Referral gives way to the dollarize chapter, as in the desktop design. */
const M2_NAV = NAV.map((item) => (item.label === 'REFERRAL' ? { label: 'DOLLARIZE', href: '#dollarize' } : item));

const ABOUT = CONTENT.about.paragraphs;
const CTO = CONTENT.aboutCto.paragraphs;
const ARTIST = CONTENT.artist.paragraphs;
const DOLLAR = CONTENT.dollarize.paragraphs;
const [TM_BEFORE, TM_AFTER] = ABOUT[3].split('has officially secured trademark');

export default function RedesignM2() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeChapter, setActiveChapter] = useState(0);
  const [comicOpen, setComicOpen] = useState(false);
  const art = pickArt(typeof window === 'undefined' ? '' : window.location.search);
  const portraitHero = art.hero.src.includes('/mobile/');
  const portraitHole = art.hole.src.includes('/mobile/');
  // the donut and its notes must fit one short phone screen with the title above
  const donut = typeof window === 'undefined' ? 200 : Math.round(Math.min(190, window.innerHeight * 0.2));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveChapter(Number(entry.target.getAttribute('data-index')));
        });
      },
      { threshold: 0.5 }
    );
    containerRef.current?.querySelectorAll('section.chapter').forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <Container ref={containerRef}>
      <RedesignFonts />
      <ProgressBar active={activeChapter} total={NUM_CHAPTERS} />

      {/* Chapter 0: Hero */}
      <Chapter className="chapter" data-index={0} bg="#0B132B" id="top">
        {portraitHero ? (
          <PortraitArt src={art.hero.src} alt="" />
        ) : (
          <HeroBg src={art.hero.src} alt="" style={{ objectPosition: art.hero.position }} />
        )}
        <Fireflies />
        <ChapterBody style={{ padding: 'env(safe-area-inset-top) 16px 0', alignItems: 'center' }}>
          <DrawnMenu />
          <TitleArt src={TITLES.zeus.src} alt={TITLES.zeus.alt} $w="80%" style={{ marginTop: 48 }} />
        </ChapterBody>
        <ThumbAction>
          <FlexCol>
            <ContractAddress tone="light" />
            <Buy big />
            <Socials color={C.white} />
          </FlexCol>
        </ThumbAction>
      </Chapter>

      <SwipeChapter id="about" index={1} bg={C.paper}>
        <Slide $center>
          <ZeusHello src="/redesign2/scenes/zeus-sit.webp" alt="Zeus, Pepe's dog, sitting" />
          <Copy $big>{ABOUT[0]}</Copy>
        </Slide>
        <Slide $center>
          <Pic src={ORIGINAL_ART} alt="The Happiness of Zeus, drawn by ndhaus as a gift for Jagged" $h="32dvh" />
          <Copy>{ABOUT[1]}</Copy>
        </Slide>
        <Slide $center bg="/redesign2/nfts/12.webp">
          <Copy $big>{ABOUT[2]}</Copy>
        </Slide>
        <Slide $center bg="/redesign2/nfts/120.webp">
          <Copy>
            {TM_BEFORE}
            <a href={CONTENT.about.trademarkLink} target="_blank" rel="noopener noreferrer">has officially secured trademark</a>
            {TM_AFTER}
          </Copy>
        </Slide>
      </SwipeChapter>

      <SwipeChapter id="takeover" index={2} bg={C.yellow} action={<Buy big />}>
        <Slide $center>
          <TitleArt src={TITLES.takeOver.src} alt={TITLES.takeOver.alt} $w="88%" style={{ alignSelf: 'center' }} />
          <Framed><Video video={VIDEOS.buy} /></Framed>
        </Slide>
        <Slide $center><Copy $big>{CTO[0]}</Copy></Slide>
        <Slide $center>
          <Copy>{CTO[1]}</Copy>
          <Copy>{CTO[2]}</Copy>
        </Slide>
      </SwipeChapter>

      <SwipeChapter id="tokenomics" index={3} bg={C.mist}>
        <Slide $center>
          <TitleArt src={TITLES.tokenomics.src} alt={TITLES.tokenomics.alt} $w="auto" style={{ alignSelf: 'center', maxWidth: '86%', maxHeight: '12dvh' }} />
          <DonutFit><SupplyDonut ink={C.ink} size={donut} circColor={C.blue} /></DonutFit>
        </Slide>
        {FACTS.map((f) => (
          <Slide key={f.key} $center>
            <FactImg src={f.art} alt="" />
            <FactTitle>{f.big}</FactTitle>
            <FactLabel>{f.label}</FactLabel>
            <FactText>{f.note}</FactText>
            <ThumbLink href={f.link} target="_blank" rel="noopener noreferrer" style={{ alignSelf: 'center' }}>Verify on Etherscan</ThumbLink>
          </Slide>
        ))}
      </SwipeChapter>

      <SwipeChapter
        id="dollarize"
        index={4}
        bg={C.ink}
        dark
        backdrop={<HeroBg src={art.hole.src} alt="" style={{ opacity: portraitHole ? 1 : 0.45 }} />}
        action={
          <FlexRow>
            <ThumbLink href={CONTENT.dollarize.mintLink} target="_blank" rel="noopener noreferrer">Mint USDZEUS</ThumbLink>
            <ThumbLink href={CONTENT.dollarize.howLink} target="_blank" rel="noopener noreferrer" outline>How it works</ThumbLink>
          </FlexRow>
        }
      >
        {portraitHole && (
          <Slide>
            <h2 style={{ margin: '8px 0 0', textAlign: 'center' }}>
              <Lettering $size="clamp(30px, 9.4vw, 40px)" style={{ lineHeight: 1.12, textWrap: 'balance' } as React.CSSProperties}>
                {CONTENT.dollarize.title}
              </Lettering>
            </h2>
          </Slide>
        )}
        <Slide $center $scrim={portraitHole}>
          {!portraitHole && <Lettering $size="clamp(30px, 9.4vw, 40px)" style={{ marginBottom: 12, lineHeight: 1.12, textWrap: 'balance' } as React.CSSProperties}>{CONTENT.dollarize.title}</Lettering>}
          <Copy $shadow>{DOLLAR[0]}</Copy>
          <Copy $shadow>{DOLLAR[1]}</Copy>
        </Slide>
        <Slide $center $scrim={portraitHole}>
          <Copy $shadow>{DOLLAR[2]}</Copy>
          <Copy $shadow>{DOLLAR[3]}</Copy>
        </Slide>
      </SwipeChapter>

      <SwipeChapter
        id="roadmap"
        index={5}
        bg={C.blue}
        dark
        header={<TitleArt src={TITLES.roadmap.src} alt={TITLES.roadmap.alt} $w="auto" style={{ alignSelf: 'center', maxWidth: '62%', maxHeight: '10dvh' }} />}
      >
        {ROADMAP.map((stop, i) => (
          <Slide key={i}>
            <ArtBox>
              <MilestoneArt data-milestone-art src={MILESTONE_ART[i % MILESTONE_ART.length]} alt="" $tilt={i % 2 ? 2 : -2} />
            </ArtBox>
            <Trail data-trail aria-hidden="true" $first={i === 0} $last={i === ROADMAP.length - 1} $done={stop.milestone.status !== 'planned'} />
            <RoadmapItem>
              {stop.pill && <Pill>{stop.pill}</Pill>}
              <h3>{stop.lines.join(' ')}</h3>
              {stop.milestone.description && <p>{stop.milestone.description}</p>}
              {stop.milestone.link && (
                <a className="go" href={stop.milestone.link.href} target="_blank" rel="noopener noreferrer">
                  {stop.milestone.link.label} ↗
                </a>
              )}
            </RoadmapItem>
            <Year data-year aria-hidden="true">{yearOf(stop)}</Year>
          </Slide>
        ))}
      </SwipeChapter>

      <SwipeChapter id="artist" index={6} bg={C.paper} action={<Buy big />}>
        <Slide $center>
          <TitleArt src={TITLES.artist.src} alt={TITLES.artist.alt} $w="78%" style={{ alignSelf: 'center' }} />
          <Framed><Video video={VIDEOS.trailer} /></Framed>
        </Slide>
        <Slide $center>
          <Pic src={NDHAUS_PHOTO} alt="ndhaus with her drawing of Zeus" $h="32dvh" />
          <Copy>{ARTIST[0]}</Copy>
        </Slide>
        <Slide $center>
          <Pic src={ORIGINAL_ART} alt="The Happiness of Zeus, the original drawing by ndhaus" $h="34dvh" />
          <Copy>{ARTIST[1]}</Copy>
        </Slide>
        <Slide $center bg="/redesign2/nfts/149.webp">
          <Copy $big>{ARTIST[2]}</Copy>
        </Slide>
        <Slide $center bg="/redesign2/nfts/146.webp">
          <Copy>{ARTIST[3]}</Copy>
          <Copy>{ARTIST[4]}</Copy>
        </Slide>
      </SwipeChapter>

      <SwipeChapter
        id="nfts"
        index={7}
        bg={C.ink}
        dark
        action={<ThumbLink href={CONTENT.nfts.link} target="_blank" rel="noopener noreferrer">View on OpenSea</ThumbLink>}
      >
        <Slide $center>
          <Lettering $size="34px" style={{ textAlign: 'center' }}>{CONTENT.nfts.title}</Lettering>
          <Pic src="/redesign2/nfts/hero.webp" alt="Dreamscape of Zeus" $h="28dvh" />
          {CONTENT.nfts.paragraphs.map((p) => <Copy key={p.slice(0, 16)}>{p}</Copy>)}
        </Slide>
        {CONTENT.nfts.items.map((id) => (
          <Slide key={id} $center>
            <NftImg src={`/redesign2/nfts/${id}.webp`} alt={`Dreamscape of Zeus #${id}`} />
          </Slide>
        ))}
      </SwipeChapter>

      <SwipeChapter
        id="memes"
        index={8}
        bg={C.ink}
        dark
        peek
        backdrop={<HeroBg src="/redesign2/scenes/swamp-empty.webp" alt="" />}
        header={<TitleArt src={TITLES.memes.src} alt={TITLES.memes.alt} $w="62%" style={{ alignSelf: 'center' }} />}
      >
        {MEMES.map((meme, i) => (
          <Slide key={meme.src} $center>
            <MemeImg src={meme.src} alt={meme.alt} role="button" title="Share on X" onClick={() => shareMeme(i, meme.alt)} />
            <ShareX type="button" onClick={() => shareMeme(i, meme.alt)}>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              Share on X
            </ShareX>
          </Slide>
        ))}
      </SwipeChapter>

      <SwipeChapter
        id="comic"
        index={9}
        bg={C.paper}
        peek
        header={
          <>
            <TitleArt src={TITLES.comic.src} alt={TITLES.comic.alt} $w="62%" style={{ alignSelf: 'center' }} />
            <ComicNote>{CONTENT.comic.subtitle}</ComicNote>
          </>
        }
        action={
          <FlexRow>
            <ThumbLink as="button" type="button" onClick={() => setComicOpen(true)}>Read</ThumbLink>
            <ThumbLink href="/zeus.pdf" target="_blank" rel="noopener noreferrer" $ink>Download PDF</ThumbLink>
          </FlexRow>
        }
      >
        {COMIC_BOOK.map((page, i) => (
          <Slide key={page.src} $center>
            <ComicPageImg src={page.src} alt={`Comic page ${i + 1}`} onClick={() => setComicOpen(true)} />
          </Slide>
        ))}
      </SwipeChapter>

      {/* Chapter 10: Join */}
      <Chapter className="chapter" data-index={10} bg={C.ink} id="join">
        <HeroBg src="/redesign2/scenes/gang.webp" alt="" style={{ objectFit: 'contain', objectPosition: 'center 30%' }} />
        <ThumbAction>
          <FlexCol>
            <Buy big />
            <Socials color={C.white} />
            <small style={{ color: C.white, fontFamily: 'Ubuntu', marginTop: 10, fontWeight: 500 }}>© 2026 Pepe's Dog CTO</small>
          </FlexCol>
        </ThumbAction>
      </Chapter>

      {comicOpen && <ComicReader pages={COMIC_BOOK} startPage={0} pdf="/zeus.pdf" onClose={() => setComicOpen(false)} />}
    </Container>
  );
}

// --- Swipe chapter ---

/**
 * A full screen chapter whose content goes sideways: every child is one slide, and a bar at
 * the bottom always says swipe and counts where you are. Nothing scrolls inside a chapter.
 */
export function SwipeChapter({
  id,
  index,
  bg,
  dark,
  peek,
  backdrop,
  header,
  action,
  children,
}: {
  id: string;
  index: number;
  bg: string;
  dark?: boolean;
  peek?: boolean;
  backdrop?: React.ReactNode;
  header?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(0);
  const total = React.Children.toArray(children).length;

  const chapterRef = useRef<HTMLElement>(null);
  const [teach, setTeach] = useState(false);

  // slides are evenly spaced, so the scroll position tells which one is in front
  const onScroll = () => {
    const t = trackRef.current;
    if (!t || total < 2) return;
    const step = (t.scrollWidth - t.clientWidth) / (total - 1);
    setAt(step > 0 ? Math.max(0, Math.min(total - 1, Math.round(t.scrollLeft / step))) : 0);
  };

  // every time a chapter comes into view, its slides lean aside once to show the next one
  useEffect(() => {
    const el = chapterRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inView = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting && !inView) {
          inView = true;
          if (timer) clearTimeout(timer);
          setTeach(true);
          timer = setTimeout(() => setTeach(false), 2600);
        } else if (!entry.isIntersecting) {
          inView = false;
          if (timer) clearTimeout(timer);
          setTeach(false);
        }
      },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  const go = (dir: 1 | -1) => {
    const t = trackRef.current;
    const next = Math.max(0, Math.min(total - 1, at + dir));
    const target = t?.children[next] as HTMLElement | undefined;
    setAt(next);
    if (t && target && typeof t.scrollTo === 'function') {
      t.scrollTo({ left: target.offsetLeft - (t.clientWidth - target.offsetWidth) / 2, behavior: 'smooth' });
    }
  };

  return (
    <Chapter className="chapter" data-index={index} bg={bg} id={id} ref={chapterRef}>
      {backdrop}
      {header && <Header $dark={dark}>{header}</Header>}
      <Track data-track ref={trackRef} onScroll={onScroll} $peek={peek} $dark={dark} $action={!!action} $header={!!header} $teach={teach && at < total - 1} data-teach={String(teach && at < total - 1)}>
        {children}
      </Track>
      {action && <ActionRow>{action}</ActionRow>}
      {total > 1 && <SwipeBar data-swipe-bar>
        {at === 0 ? (
          <SwipeCue $dark={dark}>
            <span className="word">swipe</span>
            <span className="chev" aria-hidden="true"><i>›</i><i>›</i><i>›</i></span>
          </SwipeCue>
        ) : (
          <SwipeCount $dark={dark}>
            <SwipeArrow type="button" aria-label="Previous" onClick={() => go(-1)}>‹</SwipeArrow>
            <span className="count">{`${at + 1} / ${total}`}</span>
            <SwipeArrow type="button" aria-label="Next" onClick={() => go(1)} disabled={at === total - 1}>›</SwipeArrow>
          </SwipeCount>
        )}
      </SwipeBar>}
    </Chapter>
  );
}

/** One slide. With `bg`, the words sit on a full picture of Zeus, dimmed behind them. */
export const Slide: React.FC<{ $center?: boolean; $scrim?: boolean; bg?: string; bgPosition?: string; children: React.ReactNode }> = ({ $center, $scrim, bg, bgPosition, children }) => (
  <SlideBox data-slide data-bg={bg} $center={$center} $scrim={$scrim} $bg={!!bg}>
    {bg && <SlideBg src={bg} alt="" aria-hidden="true" style={bgPosition ? { objectPosition: bgPosition } : undefined} />}
    {children}
  </SlideBox>
);

// --- Menu Component ---

export function DrawnMenu({ items = M2_NAV }: { items?: Array<{ label: string; href: string }> }) {
  const [open, setOpen] = useState(false);

  // the page behind stays still while the menu is open
  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <>
      <MenuButton onClick={() => setOpen(true)}>MENU</MenuButton>
      {/* rendered on the body: inside a chapter it would sit under the hero's contract and BUY button */}
      {open && createPortal(
        <MenuOverlay role="dialog" aria-modal="true" aria-label="Menu">
          <MenuClose onClick={() => setOpen(false)}>CLOSE</MenuClose>
          <MenuList>
            {items.map(item => (
              <li key={item.label}>
                <a href={item.href} onClick={() => setOpen(false)}>{item.label}</a>
              </li>
            ))}
            <li><span>UTILITIES</span></li>
            {UTILITIES.map(u => (
              <li key={u.label} className="sub">
                <a href={u.href} onClick={() => setOpen(false)}>{u.label}</a>
              </li>
            ))}
          </MenuList>
        </MenuOverlay>,
        document.body
      )}
    </>
  );
}

// --- Styles ---

export const Container = styled.div`
  width: 100vw;
  height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  background: ${C.ink};
  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

export const Chapter = styled.section<{ bg: string }>`
  position: relative;
  width: 100vw;
  height: 100dvh;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  background: ${p => p.bg};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const HeroBg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  pointer-events: none;
`;


/** A tall picture fitted to the width and anchored at the top, so the bottom stays free for the actions. */
const PortraitArt = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  /* never taller than the space above the actions, so Zeus is never under the contract */
  height: min(calc(100vw * 1672 / 941), calc(100dvh - 210px));
  object-fit: cover;
  object-position: center 72%;
  pointer-events: none;
  -webkit-mask-image: linear-gradient(to bottom, #000 82%, transparent);
  mask-image: linear-gradient(to bottom, #000 82%, transparent);
`;

export const ChapterBody = styled.div<{ dark?: boolean }>`
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 20px calc(env(safe-area-inset-bottom) + 120px);
  color: ${p => p.dark ? C.white : C.ink};
  padding-top: calc(env(safe-area-inset-top) + 40px);
`;


export const ThumbAction = styled.div`
  position: absolute;
  bottom: calc(env(safe-area-inset-bottom) + 30px);
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 0 20px;
  z-index: 10;
  pointer-events: none;
  & > * { pointer-events: auto; }
`;

export const ThumbLink = styled.a<{ outline?: boolean; $ink?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 16px 32px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 20px;
  color: ${p => p.outline ? C.white : C.ink};
  /* the outline button sits on pictures too: a solid dark fill keeps it readable on any of them */
  background: ${p => (p.outline ? 'rgba(11, 19, 43, 0.9)' : p.$ink ? 'transparent' : C.yellow)};
  border: 4px solid ${p => p.outline ? C.white : C.ink};
  border-radius: 999px;
  box-shadow: ${p => (p.$ink ? 'none' : `4px 5px 0 ${C.ink}`)};
  padding: 12px 26px;
  min-height: 48px;
  font-size: 18px;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  &:active { transform: translate(3px, 4px); box-shadow: ${p => p.outline ? 'none' : `1px 1px 0 ${C.ink}`}; }
`;

export const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
`;








const Pill = styled.div`
  display: inline-block;
  align-self: flex-start;
  background: ${C.yellow};
  color: ${C.ink};
  padding: 4px 12px;
  border: 3px solid ${C.ink};
  border-radius: 999px;
  font-family: 'Ubuntu Mono', monospace;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 12px;
  box-shadow: 3px 3px 0 ${C.ink};
`;


// --- Swipe chapter styles ---

const SAFE_B = 'env(safe-area-inset-bottom, 0px)';
const SAFE_T = 'env(safe-area-inset-top, 0px)';

const Header = styled.div<{ $dark?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: calc(${SAFE_T} + 34px) 16px clamp(10px, 2.4dvh, 24px);
  color: ${p => (p.$dark ? C.white : C.ink)};
`;

const Track = styled.div<{ $peek?: boolean; $dark?: boolean; $action: boolean; $header: boolean; $teach?: boolean }>`
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  padding: 0 ${p => (p.$peek ? '11vw' : '0')};
  --pt: ${p => (p.$header ? '6px' : `calc(${SAFE_T} + 30px)`)};
  --pb: calc(${SAFE_B} + ${p => (p.$action ? '150px' : '82px')});
  color: ${p => (p.$dark ? C.white : C.ink)};
  & > * { animation: ${p => (p.$teach ? css`${lean} 1.1s cubic-bezier(0.45, 0, 0.2, 1) 0.35s 2` : 'none')}; }
  @media (prefers-reduced-motion: reduce) { & > * { animation: none; } }
`;

const SlideBox = styled.div<{ $center?: boolean; $scrim?: boolean; $bg?: boolean }>`
  flex: none;
  width: 100vw;
  height: 100%;
  box-sizing: border-box;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  display: flex;
  flex-direction: column;
  justify-content: ${p => (p.$center ? 'center' : 'flex-start')};
  gap: clamp(18px, 3.4dvh, 36px);
  padding: var(--pt) 22px var(--pb);
  position: relative;
  overflow: hidden;
  ${p => p.$bg && css`
    color: ${C.white};
    & > *:not(img[aria-hidden]) { position: relative; z-index: 1; text-shadow: 0 1px 3px rgba(11, 19, 43, 0.9); }
  `}
  /* text over a full picture: the picture dims behind the words, it is not a box */
  ${p => p.$scrim && css`
    justify-content: flex-start;
    padding-top: calc(var(--pt) + 8px);
    background: linear-gradient(to bottom, rgba(11, 19, 43, 0.88), rgba(11, 19, 43, 0.72) 42%, rgba(11, 19, 43, 0) 62%);
  `}
`;

const SlideBg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  filter: brightness(0.45) saturate(1.05);
`;

const ShareX = styled.button`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 8px 20px;
  border: 3px solid ${C.ink};
  border-radius: 999px;
  background: ${C.white};
  color: ${C.ink};
  font-family: 'GROBOLD', sans-serif;
  font-size: 16px;
  box-shadow: 3px 4px 0 ${C.ink};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:active { transform: translate(2px, 3px); box-shadow: 1px 1px 0 ${C.ink}; }
`;

const ActionRow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(${SAFE_B} + 76px);
  display: flex;
  justify-content: center;
  padding: 0 16px;
  z-index: 10;
  pointer-events: none;
  & > * { pointer-events: auto; }
`;

const nudge = keyframes`
  0%, 60%, 100% { transform: translateX(0); }
  70% { transform: translateX(7px); }
  80% { transform: translateX(-2px); }
  90% { transform: translateX(4px); }
`;

const wave = keyframes`
  0%, 100% { opacity: 0.35; transform: translateX(0); }
  40% { opacity: 1; transform: translateX(4px); }
`;

const lean = keyframes`
  0%, 100% { transform: translateX(0); }
  35%, 55% { transform: translateX(-56px); }
`;

const SwipeBar = styled.div`
  position: absolute;
  left: 50%;
  bottom: calc(${SAFE_B} + 14px);
  transform: translateX(-50%);
  z-index: 10;
  white-space: nowrap;
`;

/** First slide: the word and a wave of arrows at the foot of the screen, no button. */
const SwipeCue = styled.div<{ $dark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  color: ${p => (p.$dark ? C.white : C.ink)};
  text-shadow: ${p => (p.$dark ? `0 1px 3px ${C.ink}, 0 0 10px rgba(11, 19, 43, 0.6)` : `0 1px 0 ${C.white}`)};
  pointer-events: none;
  .word {
    font-family: 'GROBOLD', sans-serif;
    font-size: 19px;
    letter-spacing: 2px;
    text-transform: uppercase;
    animation: ${nudge} 2.4s ease-in-out infinite;
  }
  .chev { display: inline-flex; font-family: 'GROBOLD', sans-serif; font-size: 34px; line-height: 1; }
  .chev i {
    font-style: normal;
    margin-left: -2px;
    color: ${C.yellow};
    -webkit-text-stroke: 2px ${C.ink};
    animation: ${wave} 1.4s ease-in-out infinite;
  }
  .chev i:nth-child(2) { animation-delay: 0.18s; }
  .chev i:nth-child(3) { animation-delay: 0.36s; }
  @media (prefers-reduced-motion: reduce) {
    .word, .chev i { animation: none; opacity: 1; }
  }
`;

/** From the second slide on: where you are and how many are left. */
const SwipeCount = styled.div<{ $dark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
  border-radius: 999px;
  border: 3px solid ${p => (p.$dark ? C.white : C.ink)};
  background: ${p => (p.$dark ? 'rgba(11, 19, 43, 0.72)' : 'rgba(255, 255, 255, 0.85)')};
  box-shadow: 3px 4px 0 ${C.ink};
  color: ${p => (p.$dark ? C.white : C.ink)};
  .count {
    font-family: 'Ubuntu', sans-serif;
    font-weight: 700;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    min-width: 52px;
    text-align: center;
  }
`;

const SwipeArrow = styled.button`
  width: 44px;
  height: 44px;
  border: none;
  background: none;
  color: inherit;
  font-family: 'GROBOLD', sans-serif;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:disabled { opacity: 0.25; cursor: default; }
  &:active:not(:disabled) { transform: scale(0.88); }
`;

export const Copy = styled.p<{ $big?: boolean; $shadow?: boolean }>`
  margin: 0;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: ${p => (p.$big ? '20px' : '17px')};
  line-height: 1.5;
  text-shadow: ${p => (p.$shadow ? `1px 1px 2px ${C.ink}` : 'none')};
  a { color: inherit; font-weight: 700; text-decoration: underline; text-decoration-thickness: 2px; }
`;

const ZeusHello = styled.img`
  display: block;
  align-self: center;
  width: auto;
  max-width: 82%;
  max-height: 42dvh;
  filter: drop-shadow(4px 6px 0 rgba(11, 19, 43, 0.18));
`;

const Framed = styled.div`
  width: 100%;
  border: 4px solid ${C.ink};
  box-shadow: 4px 5px 0 ${C.ink};
  background: ${C.ink};
  video { display: block; width: 100%; aspect-ratio: 16 / 9; object-fit: cover; }
`;

/** Pictures keep their own shape: never stretched, never letterboxed inside a frame. */
const fit = css`
  display: block;
  align-self: center;
  width: auto;
  height: auto;
  max-width: 100%;
  min-height: 0;
`;

const Pic = styled.img<{ $h: string }>`
  ${fit};
  max-height: ${p => p.$h};
  border: 4px solid ${C.ink};
  box-shadow: 4px 5px 0 ${C.ink};
`;

const NftImg = styled.img`
  ${fit};
  max-height: 100%;
  border: 4px solid ${C.yellow};
  box-shadow: 6px 6px 0 ${C.yellow};
`;

const MemeImg = styled.img`
  ${fit};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &:active { transform: scale(0.98); }
  max-height: calc(100% - 64px);
  border: 4px solid ${C.yellow};
  border-radius: 6px;
  box-shadow: 4px 5px 0 ${C.ink};
`;

const ComicPageImg = styled.img`
  ${fit};
  max-height: 100%;
  border: 3px solid ${C.ink};
  box-shadow: 4px 5px 0 ${C.ink};
  cursor: zoom-in;
`;

const ComicNote = styled.p`
  margin: 0 8px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
`;

/** The donut, its legend and its note scale down together on short screens, so they stay on one slide. */
const DonutFit = styled.div`
  align-self: center;
  text-align: center;
  .status { margin: 10px 0 clamp(12px, 2.4dvh, 22px); }
  .body { flex-direction: column; justify-content: center; gap: clamp(16px, 3dvh, 30px); }
  ul { width: fit-content; margin: 0 auto; text-align: left; gap: clamp(12px, 2.2dvh, 20px); }
  .foot { margin: clamp(14px, 3dvh, 28px) auto 0; }
  @media (max-height: 800px) { zoom: 0.84; }
  @media (max-height: 700px) { zoom: 0.74; }
`;

const FactImg = styled.img`
  ${fit};
  max-height: 26dvh;
`;

const FactTitle = styled.h3`
  margin: 0;
  font-family: 'GROBOLD', sans-serif;
  font-size: 44px;
  line-height: 1;
  text-align: center;
  color: ${C.ink};
  text-shadow: 3px 3px 0 ${C.yellow};
`;

const FactLabel = styled.div`
  font-family: 'GROBOLD', sans-serif;
  font-size: 20px;
  text-align: center;
  text-transform: uppercase;
`;

const FactText = styled.p`
  margin: 0 0 6px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 17px;
  line-height: 1.45;
  text-align: center;
`;

/** The milestone's year, big in the free space at the foot of the slide. */
const Year = styled.div`
  margin-top: auto;
  align-self: center;
  position: relative;
  z-index: 1;
  font-family: 'GROBOLD', sans-serif;
  font-size: clamp(40px, 10dvh, 112px);
  line-height: 1;
  color: ${C.yellow};
  -webkit-text-stroke: 3px ${C.ink};
  paint-order: stroke fill;
  text-shadow: 4px 5px 0 ${C.ink};
  letter-spacing: 2px;
  transform: rotate(-4deg);
  pointer-events: none;
  @media (max-height: 700px) { font-size: 0; }
`;

/** Same height on every slide, so the trail under it lines up from one slide to the next. */
const ArtBox = styled.div`
  position: relative;
  z-index: 1;
  flex: none;
  height: clamp(110px, 21dvh, 230px);
  @media (max-height: 760px) { height: 17dvh; }
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MilestoneArt = styled.img<{ $tilt: number }>`
  ${fit};
  position: relative;
  z-index: 1;
  max-height: 100%;
  max-width: 84%;
  border: 4px solid ${C.ink};
  border-radius: 6px;
  background: ${C.paper};
  box-shadow: 5px 6px 0 ${C.ink};
  transform: rotate(${p => p.$tilt}deg);
`;

/** A dashed road across the slides: each slide draws its piece, so swiping walks along it. */
const Trail = styled.div<{ $first: boolean; $last: boolean; $done: boolean }>`
  position: relative;
  z-index: 1;
  height: 26px;
  margin: 0 -22px;
  flex: none;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: ${p => (p.$first ? '50%' : '0')};
    right: ${p => (p.$last ? '50%' : '0')};
    border-top: 4px dashed rgba(255, 255, 255, 0.75);
    transform: translateY(-50%);
  }
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 4px solid ${C.ink};
    background: ${p => (p.$done ? C.yellow : C.white)};
    box-shadow: 2px 3px 0 ${C.ink};
    transform: translate(-50%, -50%);
  }
`;

const RoadmapItem = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  color: ${C.white};
  h3 { margin: 0; font-size: clamp(22px, 3dvh, 28px); line-height: 1.15; text-shadow: 2px 3px 0 ${C.ink}; font-family: 'GROBOLD', sans-serif; }
  p { margin: 0; font-family: 'Ubuntu', sans-serif; font-weight: 500; font-size: 15px; line-height: 1.42; }
  @media (max-height: 760px) { gap: 8px; p { font-size: 14px; line-height: 1.36; } }
  .go { color: ${C.yellow}; font-family: 'GROBOLD', sans-serif; font-size: 17px; text-decoration: underline; text-underline-offset: 3px; text-shadow: 1px 2px 0 ${C.ink}; }
`;

// --- Progress Bar ---
export const ProgressBar = ({ active, total }: { active: number, total: number }) => (
  <ProgressWrap>
    {Array.from({ length: total }).map((_, i) => (
      <ProgressSeg key={i} $active={i <= active} />
    ))}
  </ProgressWrap>
);

const ProgressWrap = styled.div`
  position: fixed;
  top: env(safe-area-inset-top);
  left: 10px;
  right: 10px;
  height: 6px;
  display: flex;
  gap: 6px;
  z-index: 9999;
  padding-top: 10px;
  pointer-events: none;
  filter: drop-shadow(0 2px 2px rgba(0,0,0,0.6));
`;

const ProgressSeg = styled.div<{ $active: boolean }>`
  flex: 1;
  background: ${p => p.$active ? C.yellow : 'rgba(255,255,255,0.4)'};
  border-radius: 3px;
  transition: background 0.3s;
`;

// --- Menu ---
const MenuButton = styled.button`
  color: ${C.ink};
  -webkit-tap-highlight-color: transparent;
  position: absolute;
  top: max(20px, env(safe-area-inset-top));
  right: 20px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 16px;
  padding: 10px 18px;
  background: ${C.yellow};
  border: 3px solid ${C.ink};
  border-radius: 999px;
  box-shadow: 3px 3px 0 ${C.ink};
  z-index: 20;
`;

const MenuOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: ${C.ink};
  z-index: 10000;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  padding: env(safe-area-inset-top) 20px 20px;
`;

const MenuClose = styled.button`
  color: ${C.ink};
  -webkit-tap-highlight-color: transparent;
  align-self: flex-end;
  margin-top: 20px;
  font-family: 'GROBOLD', sans-serif;
  font-size: 16px;
  padding: 10px 18px;
  background: ${C.white};
  border: 3px solid ${C.ink};
  border-radius: 999px;
  box-shadow: 3px 3px 0 ${C.ink};
`;

const MenuList = styled.ul`
  list-style: none;
  padding: 40px 20px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  a, span {
    font-family: 'GROBOLD', sans-serif;
    font-size: 32px;
    color: ${C.white};
    text-decoration: none;
  }
  span { color: ${C.yellow}; font-size: 24px; }
  .sub a {
    font-size: 24px;
    font-family: 'Ubuntu', sans-serif;
    font-weight: bold;
    padding-left: 20px;
  }
`;
